import GoodToKnowSection from 'components/GoodToKnowSection';
import Separator from 'components/Separator';
import StandardCTA from 'components/StandardCTA';
import Subheading from 'components/Subheading';
import Link from 'next/link';
import { PrizeCategories } from 'types/Prize';
import { getCta } from 'utils/common';
import { PRIZE_NUMBERS } from 'utils/constants';

export const getPrizePageDetails = (
  mainCategoryCode: string | undefined,
  choosingPrize: boolean,
  prizesWonCount: number | undefined,
  treesPlanted: number | undefined,
  trackProductClickAndGoToEnter: () => Promise<void>,
  validationResult: any,
  prizeCategories: PrizeCategories = {}
) => {
  let title,
    description,
    availabilityNotice,
    subcategories,
    metaTitle,
    metaDescription,
    bottomInfoAndFaqSection;

  switch (mainCategoryCode) {
    case 'trip':
      title = 'Trips & Stays';
      description =
        'EPIC holidays and escapes for 2 (unless specified otherwise) where YOU choose the dates';
      availabilityNotice = (
        <>
          All listed travel itineraries (including flights, hotels, activities,
          and other expenditures) are subject to change based on availability,
          price fluctuations, and unforeseen travel restrictions. We will work
          with you to create the most epic adventure possible!
        </>
      );
      subcategories = prizeCategories['trip'];
      metaTitle = 'Win Travel Prizes | Luxury Holiday Competition | DAYMADE';
      metaDescription =
        'Win epic travel prizes with DAYMADE. Competition prizes include all-expenses-paid trips, luxury holidays and once-in-a-lifetime adventures. Find out more today.';
      bottomInfoAndFaqSection = (
        <>
          <Subheading
            id="blogHeading"
            underlineColor="transparent"
            style={{
              borderLeft: '8px solid #95F5DE',
              textAlign: 'left',
              paddingLeft: '0.4em',
            }}
          >
            Win Travel <strong>Prizes</strong>
          </Subheading>

          <p>
            Looking for epic travel prizes? At DAYMADE, you have the chance to
            win an all expenses paid holiday or a trip of a lifetime to one of
            our grand destinations around the world. All our travel prizes are
            for 2 people and <strong>YOU get to choose the date</strong>.
          </p>

          <p>
            Whether you’re dreaming of a Californian Road Trip, discovering the
            Galapagos Islands, an Asian Adventure or a European city break for
            2, there is a destination and trip for everyone in our luxury
            holiday competition. With a weekly competition draw, the chance of
            winning travel prizes have never been higher.
          </p>

          <StandardCTA
            id="heroEnterNowButton"
            onClick={trackProductClickAndGoToEnter}
            style={{ minWidth: '180px', margin: '15px auto 1.7em auto' }}
            overrideLabel={undefined}
            overrideUrl={undefined}
          />

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">How can I win travel prizes?</h2>

          <p>
            We have a huge selection of travel prizes, simply {''}
            <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
              <a style={{ textDecoration: 'underline' }}>enter</a>
            </Link>
            {''} our luxury holiday competition for your chance to win. With
            travel prizes all over the world and two weekly draws, the chances
            have never been higher.
          </p>

          <Separator style={undefined} id={undefined} />

          <h3 className="questionHeading">
            What are our luxury holiday competitions?
          </h3>

          <p>
            Entering our luxury holiday competition is really simple! Pick your
            winning numbers for our {''}
            <Link href="/">
              <a style={{ textDecoration: 'underline' }}>Weekly Draws</a>
            </Link>
            {''} and watch live every Friday at 5pm. If your selected numbers
            match ours, pick your prize and get exploring! The more numbers you
            match, the bigger the travel prize.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">What travel prizes can I win?</h2>

          <p>
            Win once in a lifetime travel prizes around the world! The
            opportunities for travel are endless and with DAYMADE and you’re in
            control of what you win. Win a year's worth of travel plans or an
            all expenses paid holiday adventure around one of our hot
            destinations.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">How do I become a winner?</h2>

          <p>
            With over thousands of winners every week, what’s stopping you from
            being the next? Don’t just hear it from us, see what our {''}
            <Link href="/winners">
              <a style={{ textDecoration: 'underline' }}>winners</a>
            </Link>
            {''} have to say about their DAYMADE experience and epic travel
            prizes. {''}
            <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
              <a style={{ textDecoration: 'underline' }}>Enter</a>
            </Link>
            {''} our luxury holiday competition today for your chance to join
            our winners wall. Entered already? Check your numbers and view the{' '}
            {''}
            <Link href="/results">
              <a style={{ textDecoration: 'underline' }}>results</a>
            </Link>
            {''} from our recent competitions.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">Why DAYMADE?</h2>

          <p>
            Win life-changing travel prizes from your own home with DAYMADE. For
            us, trips and experiences are worth more than money. That’s why our
            travel prizes give you the chance to travel the world, where you
            want to go, on once-in-a-lifetime adventures, and making memories
            you’ll never forget. Learn more {''}
            <Link href="/about-us">
              <a style={{ textDecoration: 'underline' }}>about us</a>
            </Link>
            {''}, and keep to date with all our latest news, highlights, travel
            tips and more in our {''}
            <Link href="/blog">
              <a style={{ textDecoration: 'underline' }}>blog</a>
            </Link>
            {''}.
          </p>

          <Separator style={undefined} id={undefined} />

          <h3 className="questionHeading">How do we give back?</h3>

          <p>
            We love giving out amazing travel prizes, but we also believe in
            doing our bit to {''}
            <Link href="/giving-back">
              <a style={{ textDecoration: 'underline' }}>give back</a>
            </Link>
            {''}. When you play with us, you also contribute to amazing causes
            including veritree — an amazing organisation that offers a climate
            change solution by planting verifiable trees. For every DAYMADE
            purchase, we plant a tree in Kwale County (Kenya), Tanzania or
            Senegal, and have planted over{' '}
            {(treesPlanted || 0).toLocaleString()} trees to date. We’re proud
            that together with our players, we are making a difference.
          </p>

          <Separator style={undefined} id={undefined} />
        </>
      );
      break;

    case 'tech':
      title = 'Tech';
      description = 'AWESOME gadgets delivered straight to your door';
      availabilityNotice = (
        <>Please note that products are subject to availability.</>
      );
      subcategories = prizeCategories['tech'];
      metaTitle = 'Win Tech Prizes | Win Gadgets in Our Competitions | DAYMADE';
      metaDescription =
        'Win tech prizes and brand new gadgets with DAYMADE. The latest tech goodies are up for grabs, from drones and cameras to phones and bikes. Find out more today.';
      bottomInfoAndFaqSection = (
        <GoodToKnowSection
          overrideSubheading={
            <>
              Win Tech <strong>Prizes</strong>
            </>
          }
          skipFirstSeparator
        >
          <>
            <p>
              Want to get your hands on the latest gadgets and tech? Win gadgets
              and choose from the best selection of tech prizes at DAYMADE. It’s
              gadget galore at DAYMADE where YOU get to pick your favourite
              technology, from top brands.
            </p>

            <p>
              Fancy getting to work quicker and in style with an electric
              scooter? Or maybe you’re wanting to up your photography game with
              one of our camera prices and bundles? Say goodbye to bad hair days
              with the latest Dyson hair tools and explore Nintendo Switch’s,
              pizza ovens, speakers, hot tubs and so much more. There’s a gadget
              prize for everyone at DAYMADE.
            </p>

            <p>
              With a weekly prize draw, the chances have never been higher.
              Enter now to be in with a chance of copping one of our epic tech
              prizes.
            </p>
          </>

          <>
            <h2>How can you win gadgets?</h2>
            <p>
              It’s easy to win big in our online tech competitions. Simply{' '}
              <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
                <a>enter</a>
              </Link>{' '}
              online today by choosing your lucky numbers, then tune in every
              Friday at 5pm to see what lucky numbers are picked out and who our
              winners are this week. The more numbers you match, the bigger the
              prizes get.
            </p>
            <h3>What tech prizes can you win?</h3>
            <p>
              Our choice of prizes is up there with the best gadget competitions
              available to enter. There's a tech prize for every hobby and
              interest whether you’re into music, gaming, photography, cooking,
              or fancy a home office upgrade.
            </p>
          </>

          <>
            <h2>How do I become a winner?</h2>
            <p>
              There have been more than 24,000 DAYMADE{' '}
              <Link href="/winners">
                <a>winners</a>
              </Link>{' '}
              to date, winning all kinds of prizes, allowing people to live
              their best lives.
              <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
                <a>Enter</a>
              </Link>{' '}
              our gadget competition today for your chance to join our winner’s
              wall and enjoy the latest technology.
            </p>
          </>

          <>
            <h2>Why DAYMADE?</h2>
            <p>
              What makes DAYMADE different from other tech competitions? We
              value trips, experiences and memories more than money, which is
              how we choose our choice of amazing tech prizes to be won. Our
              gadgets can bring joy and fun into your everyday life. Learn more{' '}
              <Link href="/about-us">
                <a>about us</a>
              </Link>
              , and keep to date with all our latest news, highlights, travel
              tips and more in our{' '}
              <Link href="/blog">
                <a>blog</a>
              </Link>
              .
            </p>
            <h3>How do we help you win tech prizes and give back?</h3>
            <p>
              We love helping you win gadgets, but we also believe in doing our
              bit to{' '}
              <Link href="/giving-back">
                <a>give back</a>
              </Link>
              . That’s why, when you play with us you don’t just have the
              opportunity to experience great things, you can also be a part of
              contributing to amazing causes including veritree - an amazing
              organisation that offers a climate change solution by planting
              verifiable trees. For every DAYMADE purchase, we plant a tree in
              Kwale County (Kenya), Tanzania or Senegal. We’re proud to have
              planted over {(treesPlanted || 0).toLocaleString()} trees to date,
              and that with our players, we’re all making a difference.
            </p>
          </>
        </GoodToKnowSection>
      );
      break;

    case 'lifestyle':
      title = 'Lifestyle';
      description = 'Home, lifestyle, beauty, hampers, fashion & more!';
      availabilityNotice = (
        <>Please note that products are subject to availability.</>
      );
      subcategories = prizeCategories['lifestyle'];
      metaTitle = 'Lifestyle Prizes | Win Beauty Prizes & Gift Cards | DAYMADE';
      metaDescription =
        'Win amazing lifestyle prizes with DAYMADE. From gift cards and beauty prizes to product subscriptions and more. Discover how you can become a winner today.';
      bottomInfoAndFaqSection = (
        <>
          <Subheading
            id="blogHeading"
            underlineColor="transparent"
            style={{
              borderLeft: '8px solid #95F5DE',
              textAlign: 'left',
              paddingLeft: '0.4em',
            }}
          >
            Win Beauty & Lifestyle <strong>Prizes</strong>
          </Subheading>

          <p>
            Win epic lifestyle prizes with DAYMADE, from cruises and travel
            bundles to home makeovers and food hampers! We also have LOADS of
            beauty prizes and gift cards. How would you like a shopping spree
            with your favourite brand? Or could we tempt you to a luxury pamper
            day with your bestie? With so much on offer, you’re certain to find
            something you love.
          </p>

          <p>
            We love making winners' dreams come true, and you could be the next
            to win one of our top beauty and lifestyle prizes. What are you
            waiting for? Get involved today!
          </p>

          <StandardCTA
            id="heroEnterNowButton"
            onClick={trackProductClickAndGoToEnter}
            style={{ minWidth: '180px', margin: '15px auto 1.7em auto' }}
            overrideLabel={undefined}
            overrideUrl={undefined}
          />

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">How do I win lifestyle prizes?</h2>

          <p>
            Our {''}
            <Link href="/">
              <a style={{ textDecoration: 'underline' }}>Weekly Draws</a>
            </Link>
            {''} are your chance to win lifestyle, beauty and gift card prizes
            every single Tuesday & Friday! Tune into the live draw at 5pm every
            Friday to see if you’ve won. Matching just two numbers can get you a
            prize. Prizes get more epic with every extra number you match!
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">What lifestyle prizes can I win?</h2>

          <p>
            Our lifestyle prizes feature fun gifts and epic experiences. Fancy a
            chef bundle worth £10,000? A personal development package to help
            you grow? How about a top of the range Nikon camera so you can get
            creative?
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">What beauty prizes can I win?</h2>

          <p>
            Our beauty prizes are just as exciting. Get a queen bundle bursting
            with luxury Harrods products, a Birchbox subscription for beauty
            treats every week and so much more. Our beauty prizes will leave you
            looking, and feeling amazing.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">What gift card prizes can I win?</h2>

          <p>
            Win gift card prizes to spend at your favourite shops, like IKEA,
            Birdsong, Jo Malone and Etsy. There's a gift card prize for everyone
            and to cater for all tastes.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">How do I win with DAYMADE?</h2>

          <p>
            We’ve had loads of {''}
            <Link href="/winners">
              <a style={{ textDecoration: 'underline' }}>winners</a>
            </Link>{' '}
            and you could be the next! It’s easy to {''}
            <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
              <a style={{ textDecoration: 'underline' }}>enter</a>
            </Link>
            {''}; what’s stopping you? Check the {''}
            <Link href="/results">
              <a style={{ textDecoration: 'underline' }}>results</a>
            </Link>
            {''} from our recent competitions to see if you’re already a winner.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">Why should I play with DAYMADE?</h2>

          <p>
            If you know anything {''}
            <Link href="/about-us">
              <a style={{ textDecoration: 'underline' }}>about DAYMADE</a>
            </Link>
            , you know that we want our players to win something special. We
            have hundreds of prizes, with various ways to win. Check out our{' '}
            {''}
            <Link href="/blog">
              <a style={{ textDecoration: 'underline' }}>blog</a>
            </Link>
            {''} for travel tips, fun features, and more.
          </p>

          <Separator style={undefined} id={undefined} />

          <h3 className="questionHeading">
            Are there any competitions that go to a good cause?
          </h3>

          <p>
            Yes! We think it’s important to {''}
            <Link href="/giving-back">
              <a style={{ textDecoration: 'underline' }}>give back</a>
            </Link>
            {''}, so we’ve partnered with {''}
            <Link href="https://www.veritree.com">
              <a style={{ textDecoration: 'underline' }} target="_blank">
                veritree
              </a>
            </Link>
            {''} — an amazing organisation that offers a climate change solution
            by planting verifiable trees. We plant a tree in Kwale County
            (Kenya), Tanzania or Senegal with every DAYMADE purchase, with more
            than {(treesPlanted || 10).toLocaleString()} planted to date. If you
            want to make a difference, play with DAYMADE.
          </p>

          <Separator style={undefined} id={undefined} />

          <h2 className="questionHeading">
            What prizes would you like to see?
          </h2>

          <p>
            What’s your perfect prize? We all have a dream holiday or experience
            and we love hearing suggestions, so please {''}
            <Link href="mailto:hello@daymade.co.uk">
              <a style={{ textDecoration: 'underline' }}>let us know</a>
            </Link>
            ! You could end up winning it...
          </p>

          <Separator style={undefined} id={undefined} />
        </>
      );
      break;

    case 'experience':
      title = 'Experiences';
      description = 'EPIC experiences from bottomless brunch to bungee jumping';
      availabilityNotice = (
        <>
          Please note that products are subject to availability.
          <br />
          <br />
          All listed travel itineraries (including flights, hotels, activities,
          and other expenditures) are subject to change based on availability,
          price fluctuations, and unforeseen travel restrictions. We will work
          with you to create the most epic adventure possible!
        </>
      );
      subcategories = prizeCategories['experience'];
      metaTitle = 'Win Experiences | Once-in-a-Lifetime Adventures | DAYMADE';
      metaDescription =
        'Win once-in-a-lifetime experiences with DAYMADE. From Galapagos adventures and jungle expeditions to flying lessons and bottomless brunch. Discover more here.';
      bottomInfoAndFaqSection = (
        <GoodToKnowSection
          overrideSubheading={
            <>
              Win <strong>Experiences</strong>
            </>
          }
          skipFirstSeparator
        >
          <>
            <p>
              Looking to win experiences and once-in-a-lifetime adventures?
              Enter our competitions for your chance to go on trips beyond your
              wildest dreams! Our prizes are for two people, meaning you can
              share your experience with whoever you like!
            </p>

            <p>
              Whether you're longing for a Colombian jungle expedition, an
              Arctic Circle adventure, a hiking excursion in the Slovenian Alps
              or £25k to plan your own travel for a whole year, DAYMADE is the
              best place to win experiences. We offer a variety of prizes
              starting from just £25, so there are plenty of ways to win! Our
              weekly draws make winning experiences simple.
            </p>
          </>

          <>
            <h2>How can you win experiences?</h2>
            <p>
              To win experiences with DAYMADE, just{' '}
              <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
                <a>enter</a>
              </Link>{' '}
              our competition - it could be your lucky day! With our weekly
              draws and so many prizes on offer, the chances of winning have
              never been higher.
            </p>
            <h3>What are our experiences competitions?</h3>
            <p>
              We pick the winner of our{' '}
              <Link href="/">
                <a>weekly draws</a>
              </Link>{' '}
              every Tuesday and Friday at 5pm - be sure to watch live on
              Instagram! If it’s your lucky day and your numbers come up, you
              could be in for the experience of a lifetime. The more numbers you
              match, the bigger the experience you’ll win!
            </p>
          </>

          <>
            <h2>What experiences can I win?</h2>
            <p>
              With prizes ranging from £25 to £25k, there are LOADS of
              opportunities to win experiences with DAYMADE. From comedy nights,
              spa days and concerts to flying lessons, bungee jumps and Everest
              treks, there’s something guaranteed for everyone. You’ll be able
              to choose between prizes associated with the amount of numbers
              you’ve matched, so you can pick something you’ve always wanted.
            </p>
          </>

          <>
            <h2>How do I win with DAYMADE?</h2>
            <p>
              To this day, we’ve made over 27,000 winners - there’s no reason
              why you shouldn’t be the next.
              <Link href="/winners">
                <a>Meet our winners</a>
              </Link>{' '}
              to hear what they have to say about DAYMADE and their prizes, and{' '}
              <Link href={getCta()?.url || '#errorPrizesPageDetails'}>
                <a>enter</a>
              </Link>{' '}
              today for your chance to join our hall of fame! And if you’ve
              entered already and still haven’t checked your numbers, view the{' '}
              <Link href="/results">
                <a>results</a>
              </Link>{' '}
              to see if you’re a winner.
            </p>
          </>

          <>
            <h2>Why play with DAYMADE?</h2>
            <p>
              With DAYMADE, you can win experiences that you’ll never forget
              from your own home. We believe that experiences are more valuable
              than money, and our prizes reflect this. Whatever you win with
              DAYMADE, you can be sure it’ll make life richer and more fun! Read
              more{' '}
              <Link href="/about-us">
                <a>about us</a>
              </Link>{' '}
              and our ethos, and view our{' '}
              <Link href="/blog">
                <a>blog</a>
              </Link>{' '}
              for the latest updates, travel tips and highlights.
            </p>
            <h3>How do we give back?</h3>
            <p>
              Giving our players epic experiences is just one part of what we
              do: we also believe in{' '}
              <Link href="/giving-back">
                <a>giving back</a>
              </Link>{' '}
              to those who need it most. All our players contribute to great
              causes, such as veritree. veritree is an amazing organisation that
              offers a climate change solution by planting verifiable trees. We
              plant a tree in Kwale County (Kenya), Tanzania or Senegal every
              time you make a DAYMADE purchase. And with over{' '}
              {(treesPlanted || 10).toLocaleString()} trees planted to date,
              we’re incredibly proud to be making a difference with all our
              amazing players.
            </p>
          </>
        </GoodToKnowSection>
      );
      break;

    case 'family':
      title = 'Family';
      description = 'Win epic prizes for the whole family!';
      subcategories = prizeCategories['family'];
      metaTitle =
        'Win Prizes for the whole family | Unforgettable holidays & must-haves | DAYMADE';
      metaDescription =
        'Win unforgettable family holidays, experiences and fun gifts with DAYMADE. From a family holiday to the giraffe manor in Nairobi, to a weekend Center Parks to tickets for Alton Towers. Discover more here.';
      break;

    default:
      title = 'All Prizes';
      description = `${PRIZE_NUMBERS.PREMIUM} EPIC prizes from beauty boxes to brunch in Bali. Choose between trips, lifestyle prizes, tech prizes or experiences above - or check them all out below!`;
      availabilityNotice = (
        <>
          Please note that products are subject to availability.
          {(!choosingPrize || validationResult?.prizeValue > 100) && ( // Only include following message if not redeeming a prize, or if the prize being redeemed is worth more than £100
            <>
              <br />
              <br />
              All listed travel itineraries (including flights, hotels,
              activities, and other expenditures) are subject to change based on
              availability, price fluctuations, and unforeseen travel
              restrictions. We will work with you to create the most epic
              adventure possible!
            </>
          )}
        </>
      );
      metaTitle = 'Win Prizes Online | Trips, Experiences and More | DAYMADE';
      metaDescription =
        'Win prizes online with DAYMADE. From gift cards and gadgets to the holidays of a lifetime. Find out how you could be going on your next adventure here.';
      subcategories = null;
  }

  return {
    title,
    description,
    availabilityNotice,
    subcategories,
    metaTitle,
    metaDescription,
    bottomInfoAndFaqSection,
    isPrizesPage: true,
  };
};
